/**
 * Verification Banner
 *
 * Shown under the navbar while the signed-in user's email is unverified
 * (framing S-D1, S-D2). Truthful about whether the email was sent; offers
 * Resend and Contact. Dismissible for the session; returns next session.
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import '../Funnel.css';

const DISMISS_KEY = 'sigtdd_verify_banner_dismissed';

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

interface VerificationBannerProps {
  onOpenContact?: () => void;
}

export default function VerificationBanner({ onOpenContact }: VerificationBannerProps) {
  const { user, verificationEmailSent, resendVerification } = useAuth();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (!user || user.email_verified || dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const resend = async () => {
    setSending(true);
    setNotice(null);
    const result = await resendVerification();
    setNotice(result.message);
    setSending(false);
  };

  const notSent = verificationEmailSent === false;

  return (
    <div className={`verify-banner${notSent ? ' verify-banner-warn' : ''}`} role="status">
      <span className="verify-banner-text">
        {notSent
          ? <>We could not send the verification email to <strong>{user.email}</strong>. Request it again, or contact support.</>
          : <>Verify your email — we sent a link to <strong>{user.email}</strong>. Saving and sharing already work.</>}
        {notice && <> {notice}</>}
      </span>
      <span className="verify-banner-actions">
        <button className="verify-banner-btn" onClick={resend} disabled={sending}>
          {sending ? 'Sending…' : 'Resend'}
        </button>
        {notSent && onOpenContact && (
          <button className="verify-banner-btn" onClick={onOpenContact}>Contact</button>
        )}
        <button className="verify-banner-dismiss" onClick={dismiss} aria-label="Dismiss" title="Dismiss for this session">×</button>
      </span>
    </div>
  );
}
